import React, { useEffect, useRef, useState } from 'react';
import useAuth from '@maxis/client-logic/hooks/useAuth';
import { Button, TextInput } from '@maxis/components/components';
import style from './InitSetting.module.css';
import { useNavigate } from 'react-router-dom';
import { FormSection } from '@maxis/components';
import useFetch from '@maxis/client-logic/hooks/useFetch';
import { useHookForm } from '@maxis/client-logic/hooks/useHookForm';
import useMessageBox from '@maxis/client-logic/hooks/useMessageBox';
import { validate } from '@maxis/client-logic/utils/validationUtil';
import { isEmpty } from 'lodash';
import { toCamelCase } from '@maxis/client-logic/utils/dataUtil';
import model_data from './Service/SRV300/public_litellm/model_prices_and_context_window_backup.json';
import provider_fields from './Service/SRV300/public_litellm/provider_create_fields.json';
import { LOCAL_STORAGE_KEYS } from '@maxis/client-logic/constants/enumerates';
import { VALIDATION_TYPE } from '@maxis/client-logic/constants/validationType';
import { getModelKeysForProviderSlug, normalizeProviderSlug, toSelectOptions, ModelModes } from './Service/SRV300/LlmProviders';
import {
    PageContainer,
    FlexContainer,
    WrapContainer,
    FlexItemContainer,
    LeftWrap,
    FlexItem,
} from '@maxis/components';
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
} from '@maxis/client-logic/utils/localStorageUtil';

const PAGES = {
    START: 'start',
    ADMIN: 'admin',
    CHAT: 'chat',
    EMB: 'emb',
    CONFIRM: 'confirm'
};

const PAGE_FLOW = {
    [PAGES.START]: { prev: null, next: PAGES.ADMIN },
    [PAGES.ADMIN]: { prev: null, next: PAGES.CHAT },
    [PAGES.CHAT]: { prev: PAGES.ADMIN, next: PAGES.EMB },
    [PAGES.EMB]: { prev: PAGES.CHAT, next: PAGES.CONFIRM },
    [PAGES.CONFIRM]: { prev: PAGES.EMB, next: null }
};

function InitSetting() {
    const { alert, confirm } = useMessageBox();
    const { login } = useAuth();
    const { request, saveRequest } = useFetch();
    const navigate = useNavigate();
    
    const [settingEnabled, setSettingEnabled] = useState(false);
    const [hiddenModel, setHiddenModel] = useState(false);
    const [dynamicFormControls, setDynamicFormControls] = useState({});
    const dynamicRefs = useRef({});
    /** modelReset()으로 값 주입 중에만 true — 넓은 400ms 윈도우 쓰면 사용자가 Provider 바꿀 때 초기화/바인딩이 스킵됨 */
    const isApplyingModelResetRef = useRef(false);
    const shouldRestoreDynamicRef = useRef(false);

    const buildDynamicControls = (providerName) => {
        if (!providerName) {
            setDynamicFormControls({});
            return;
        }
        const providerData = provider_fields.find(p => p.litellm_provider === providerName);
        if (!providerData?.credential_fields?.length) {
            setDynamicFormControls({});
            return;
        }
        const prefixMap = { text: 'txt', password: 'txt', textarea: 'txa', select: 'cbo', upload: 'file' };

        const newControls = {};
        providerData.credential_fields.forEach(field => {
            const prefix = prefixMap[field.field_type] || 'txt';
            const fieldName = toCamelCase(field.key);
            const refKey = `${prefix}${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;

            if (!dynamicRefs.current[refKey]) {
                dynamicRefs.current[refKey] = React.createRef();
            }
            newControls[refKey] = {
                ref: dynamicRefs.current[refKey],
                label: field.label,
                labelWidth: 'wpx_150',
                formField: field.key.toUpperCase(),
                ...(field.tooltip && { tooltip: true, tooltipMessage: field.tooltip }),
                controlOption: {
                    className: 'stc-border-radius-1-0',
                    ...(field.required && { validation: { required: field.required }}),
                    ...(field.placeholder && { placeholder: field.placeholder }),
                    ...(field.field_type === 'password' && { type: 'password' }),
                    ...(field.field_type === 'select' && {
                        dataSource: (field.options || []).map(opt => ({ label: opt, value: opt })),
                        labelId: 'label',
                        valueId: 'value',
                        addBlank: true,
                    }),
                },
            };
        });
        setDynamicFormControls({ formControls: newControls, numOfCols: 1 });
    };
    const [currentPage, setCurrentPage] = useState(PAGES.START);
    const [formData, setFormData] = useState({
        admin: null, chat: null, emb: null
    })
    useEffect(() => {
        pageFunctions.checkUserEmpty()
    }, [navigate]);

    useEffect(() => {
        setTimeout(() => {
            const data = currentPage === PAGES.ADMIN ? formData.admin : 
                        currentPage === PAGES.CHAT ? formData.chat : 
                        currentPage === PAGES.EMB ? formData.emb : null;

            if (currentPage === PAGES.ADMIN){
                adminReset(data)
            } else if (currentPage === PAGES.CHAT || currentPage === PAGES.EMB) {
                const enabled = data?.SETTING_ENABLED === true ? true : false;
                isApplyingModelResetRef.current = true;
                modelReset(data);
                queueMicrotask(() => {
                    isApplyingModelResetRef.current = false;
                });
                setSettingEnabled(enabled)
                setTimeout(() => {
                    swReset(data)
                }, 100)
                if (!data?.CUSTOM_LLM_PROVIDER) {
                    setDynamicFormControls({});
                }
            }
        }, 100);     
    }, [currentPage, formData]);

    useEffect(() => {
        if (currentPage !== PAGES.CHAT && currentPage !== PAGES.EMB) return;
        const data = currentPage === PAGES.CHAT ? formData.chat : formData.emb;
        if (!data?.CUSTOM_LLM_PROVIDER) {
            setDynamicFormControls({});
            shouldRestoreDynamicRef.current = false;
            return;
        }
        shouldRestoreDynamicRef.current = true;
        const p =
            data.CUSTOM_LLM_PROVIDER === 'openai-compatible'
                ? 'openai'
                : data.CUSTOM_LLM_PROVIDER;
        buildDynamicControls(p);
    }, [currentPage, formData.chat, formData.emb]);

    useEffect(() => {
        if (currentPage !== PAGES.CHAT && currentPage !== PAGES.EMB) return;
        if (!dynamicFormControls?.formControls || isEmpty(dynamicFormControls.formControls)) return;
        const data = currentPage === PAGES.CHAT ? formData.chat : formData.emb;
        if (isEmpty(data) || !shouldRestoreDynamicRef.current) return;

        setTimeout(() => {
            if (!shouldRestoreDynamicRef.current) return;
            Object.values(dynamicFormControls.formControls).forEach(control => {
                const key = control.formField.toLowerCase();
                const value =
                    data?.LITELLM_PARAMS?.[key] ??
                    data[control.formField] ??
                    data[key];
                if (value !== undefined && value !== null) {
                    control.ref.current?.setValue(value);
                }
            });
            shouldRestoreDynamicRef.current = false;
        }, 350);
    }, [dynamicFormControls, currentPage, formData.chat, formData.emb]);

    const controls = {
        adminForm: {
            txtUserId: {
                ref: useRef(),
                label: 'ID',
                formField: 'USER_ID',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true },
                controlOption: {
                    placeholder: 'ID를 입력하세요.',
                },
            },
            txtUserNm: {
                ref: useRef(),
                label: '사용자명',
                formField: 'USER_NM',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true },
                controlOption: {
                    placeholder: '사용자명을 입력하세요.',
                },
            },
            txtUserEmail: {
                ref: useRef(),
                label: '이메일주소',
                formField: 'USER_EMAIL',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true, type: VALIDATION_TYPE.EMAIL },
                controlOption: {
                    placeholder: '이메일을 입력하세요.',
                },
            },
            txtPassword: {
                ref: useRef(),
                label: '비밀번호',
                formField: 'PASSWORD',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true },
                controlOption: {
                    type: 'password',
                    placeholder: '비밀번호를 입력하세요.',
                },
            },
            txtChkPassword: {
                ref: useRef(),
                label: '비밀번호 확인',
                formField: 'CHK_PASSWORD',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true },
                controlOption: {
                    type: 'password',
                    placeholder: '비밀번호를 다시 입력하세요.',
                },
            },
        },
        modelForm: {
            txtId: {
                ref: useRef(),
                label: 'Model ID',
                labelWidth: '',
                controlWidth: '',
                isKey: true,
                formField: 'ID',
                controlOption: {
                    hidden: true,
                },
            },
            selCustomLlmProvider: {
                ref: useRef(),
                label: 'Provider',
                labelWidth: 'wpx_150',
                formField: 'CUSTOM_LLM_PROVIDER',
                validation: { required: true },
                controlOption: {
                    className: 'stc-border-radius-1-0',
                    dataSource: provider_fields.map(p => ({ label: p.provider_display_name, value: p.litellm_provider })),
                    labelId: 'label',
                    valueId: 'value',
                    addBlank: true,
                    onChange: (_option, found) => {
                        const provider = found?.value === 'openai-compatible' ? 'openai' : found?.value;
                        buildDynamicControls(provider);
                        if (!isApplyingModelResetRef.current) {
                            shouldRestoreDynamicRef.current = false;
                            controls.modelForm.selModel.ref.current?.setValue('');
                            controls.modelForm.txtCustomModelName.ref.current?.setValue('');
                            controls.modelForm.txtModelName.ref.current?.setValue('');
                            controls.switchForm.swiSettingEnabled.ref.current?.setValue(false);
                            controls.switchForm.txtInputCostPerToken.ref.current?.setValue('');
                            controls.switchForm.txtOutputCostPerToken.ref.current?.setValue('');
                            setSettingEnabled(false);
                        }
                        const mode = currentPage === PAGES.EMB ? 'embedding' : 'chat';
                        const slug = normalizeProviderSlug(provider);
                        const keys = getModelKeysForProviderSlug(model_data, slug)
                            .filter(key => model_data[key]?.mode === mode);
                        if (!keys.length) {
                            setHiddenModel(true);
                        } else {
                            setHiddenModel(false);
                            const applyBinding = () =>
                                controls.modelForm.selModel.ref.current?.setDataBinding(
                                    toSelectOptions(keys),
                                    ''
                                );
                            applyBinding();
                            setTimeout(applyBinding, 0);
                        }
                    },
                },
            },
            cboMode: {
                ref: useRef(),
                label: '모델타입',
                formField: 'MODE',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                controlOption: {
                    className: 'stc-border-radius-1-0',
                    dataSource: ModelModes,
                    labelId: 'label',
                    valueId: 'value',
                    defaultValue: currentPage === PAGES.CHAT ? 'chat' : currentPage === PAGES.EMB ? 'embedding' : 'chat',
                    disabled: true,
                },
            },
            selModel: {
                ref: useRef(),
                label: '모델선택',
                formField: 'MODEL',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                controlOption: {
                    addBlank: true,
                    className: 'stc-border-radius-1-0',
                    labelId: 'label',
                    valueId: 'value',
                    hidden: hiddenModel,
                    // SelectField: setValue/setDataBinding 시 onChange 호출 → 복원 중에는 표시명 덮어쓰기 방지
                    onChange: (_option, found) => {
                        if (isApplyingModelResetRef.current) return;
                        const v = found?.value ?? '';
                        controls.modelForm.txtCustomModelName.ref.current?.setValue(v);
                        controls.modelForm.txtModelName.ref.current?.setValue(v);
                    },
                },
            },
            txtCustomModelName: {
                ref: useRef(),
                label: 'Model Name',
                formField: 'CUSTOM_MODEL_NAME',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true },
                controlOption: {
                    className: 'stc-border-radius-1-0',
                    onChange: (e) => {
                        controls.modelForm.txtModelName.ref.current?.setValue(e.target.value);
                    },
                },
            },
            txtModelName: {
                ref: useRef(),
                label: 'Display Model Name',
                formField: 'MODEL_NAME',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                validation: { required: true },
                controlOption: {
                    className: 'stc-border-radius-1-0',
                },
            },
            
        },
        switchForm: {
            swiSettingEnabled: {
                ref: useRef(),
                label: '추가 설정',
                formField: 'SETTING_ENABLED',
                labelWidth: 'wpx_150',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                controlType: 'sw',
                controlOption: {
                    textLabel: '',
                    onChange: (e) => {
                        const isEnabled = e.target.checked;
                        setSettingEnabled(isEnabled);
                    },
                },
            },
            txtInputCostPerToken: {
                ref: useRef(),
                label: '입력요금(1M)',
                formField: 'INPUT_COST_PER_TOKEN',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                labelWidth: 'wpx_150',
                controlOption: {
                    className: 'stc-border-radius-1-0',
                    hidden: !settingEnabled,
                },
            },
            txtOutputCostPerToken: {
                ref: useRef(),
                label: '출력요금(1M)',
                formField: 'OUTPUT_COST_PER_TOKEN',
                //thClass: 'bkNoBorder',
                //tdClass: 'bkNoBorder',
                labelWidth: 'wpx_150',
                controlOption: {
                    className: 'stc-border-radius-1-0',
                    hidden: !settingEnabled,
                },
            },
        },
    };
    
    const {
        reset: modelReset,
        getValues: modelGetValues,
    } = useHookForm(controls.modelForm);

    const {
        reset: swReset,
        getValues: swGetValues,
    } = useHookForm(controls.switchForm);

    const {
        reset: adminReset,
        getValues: adminGetValues,
    } = useHookForm(controls.adminForm);

    const pageFunctions = {
        checkUserEmpty: async () => {
            const response = await request("/init/check-user-empty", {}, {showLoad:false});
            if (!response){
                navigate(import.meta.env.VITE_PUBLIC_URL + '/', { replace: true });
            }
        },
        adminValidChk: async () => {
            const newPW = controls.adminForm.txtPassword.ref;
            const chkPW = controls.adminForm.txtChkPassword.ref;
            try {
                if (validate(controls.adminForm)) {
                    if (newPW.current?.getValue() !== chkPW.current?.getValue()) {
                        throw new Error('비밀번호 확인이 일치하지 않습니다.');
                    }
                }
                const chkPWValue = chkPW.current?.getValue();
                if (chkPWValue.length < 10) {
                    throw new Error('비밀번호는 10자 이상입니다.');
                } else if (chkPWValue.length > 30) {
                    throw new Error('비밀번호는 30자 이하입니다.');
                }

                if ((chkPWValue.match(/[\p{Script=Hangul}]/u) || []).length) {
                    throw new Error('비밀번호에 한글은 사용할 수 없습니다.');
                } else {
                    const chkItem = {
                        digit: (chkPWValue.match(/\d/g) || []).length,
                        lower: (chkPWValue.match(/[a-z]/g) || []).length,
                        upper: (chkPWValue.match(/[A-Z]/g) || []).length,
                        special: (
                            chkPWValue.match(
                                /[\!\@\#\$\%\^\&\*\(\)\-\_\=\~\:\;\<\>\.\,\{\}\[\]\\\|\'\`\?]/g
                            ) || []
                        ).length,
                    };

                    let cnt = 0;
                    for (let i in chkItem) {
                        if (chkItem[i] > 0) cnt++;
                    }
                    if (cnt < 3) {
                        throw new Error(
                            '비밀번호는 대문자, 소문자, 특수문자, 숫자 중 3가지 이상 조합해야 합니다.'
                        );
                    }
                }
                return true;
            } catch (e) {
                alert('', e.message);
                return false;
            }
        },
        modelValidChk: async () => {
            try {
                if (validate(controls.modelForm)) {
                    return true;
                }
            } catch (e) {
                alert('', e.message);
                return false;
            }
        },
        saveInitData: async () => {
            try {
                if (formData.chat?.MODEL_NAME.length > 30 || formData.emb?.MODEL_NAME.length > 30){
                    alert("확인", "Display Model Name 은 30자를 초과할수 없습니다.");
                    return
                }
                const response1 = await saveRequest(
                    `/init/insertAdmin`,
                    { method: 'POST', body: formData.admin}
                );
                if (!response1) return;
                
                await pageFunctions.login()

                let response2 = null;
                let response3 = null;

                if (formData.chat) {
                    response2 = await request(null, {
                        ...formData.chat,
                        serviceId: 'Srv310Service',
                        process: [{ createLlm: 'Model 생성/수정' }],
                    });
                }
                if (formData.emb) {
                    response3 = await request(null, {
                        ...formData.emb,
                        serviceId: 'Srv310Service',
                        process: [{ createLlm: 'Model 생성/수정' }],
                    });
                }

                // 모든 요청의 성공 여부 체크
                if (response1?.data && response2?.data && response3?.data) {
                    alert('초기 설정이 성공적으로 완료되었습니다.');
                } else if (response1?.data && response2?.data) {
                    alert('채팅 모델 생성에 실패했습니다. LLM 관리에서 다시 등록해주세요.');
                } else if (response1?.data && response3?.data) {
                    alert('임베딩 모델 생성에 실패했습니다. LLM 관리에서 다시 등록해주세요.');
                }
                if (response1){
                    navigate(import.meta.env.VITE_PUBLIC_URL + '/', { replace: true })
                }
                
            } catch (error) {
                console.error('초기 설정 저장 실패:', error);
                alert('초기 설정 저장에 실패했습니다: ');
            }
        },
        login: async () => {
            try {
                setLocalStorageItem(
                    LOCAL_STORAGE_KEYS.SAVED_LOGIN_ID,
                    formData.admin.USER_ID
                );
                await login({
                    userId: formData.admin.USER_ID,
                    password: formData.admin.PASSWORD,
                });
            } catch (e) {
                alert('', e.message);
            }
        },
        pageSetting: async (action) => {
            const flow = PAGE_FLOW[currentPage];
            if (!flow) return;
            // next와 prev 때 데이터 저장
            if (action === 'next' || action === 'prev') {
                let isValid = false;
                let data = null;
                if (currentPage === PAGES.ADMIN) {
                    // next일 때만 유효성 검사, prev일 때는 검사 없이 저장
                    if (action === 'next') {
                        isValid = await pageFunctions.adminValidChk();
                    } else {
                        isValid = true; // prev일 때는 항상 true
                    }
                    if (isValid) {
                        data = adminGetValues();
                        setFormData(prev => ({ ...prev, admin: data }));
                    }
                } else if (currentPage === PAGES.CHAT || currentPage === PAGES.EMB) {
                    // next일 때만 유효성 검사, prev일 때는 검사 없이 저장
                    if (action === 'next') {
                        isValid = await pageFunctions.modelValidChk();
                    } else {
                        isValid = true; // prev일 때는 항상 true
                    }
                    if (isValid) {
                        const dynamicParam = Object.fromEntries(
                            Object.values(dynamicFormControls?.formControls || {}).map(
                                control => [
                                    control.formField.toLowerCase(),
                                    control.ref.current?.getValue?.(),
                                ]
                            )
                        );
                        data = {
                            ...modelGetValues(),
                            ...swGetValues(),
                            LITELLM_PARAMS: dynamicParam,
                        };
                        const dataKey = currentPage === PAGES.CHAT ? 'chat' : 'emb';
                        setFormData(prev => ({ ...prev, [dataKey]: data }));
                    }
                } else if (currentPage === PAGES.CONFIRM) {
                    // CONFIRM 페이지에서는 데이터 저장 없이 바로 이동
                    isValid = true;
                }
                if (!isValid) {
                    return;
                }
            }
            // 페이지 이동 로직
            let targetPage = null;
            
            if (action === 'prev') {
                targetPage = flow.prev;
            } else if (action === 'next') {
                targetPage = flow.next;
            } else if (action === 'skip') {
                // 건너뛰기: 현재 페이지 데이터를 null로 설정하고 다음 페이지로 이동
                if (currentPage === PAGES.CHAT) {
                    setFormData(prev => ({ ...prev, chat: null }));
                    targetPage = flow.next;
                } else if (currentPage === PAGES.EMB) {
                    setFormData(prev => ({ ...prev, emb: null }));
                    targetPage = flow.next;
                }
            }
            if (targetPage) {
                setCurrentPage(targetPage);
            }
        }
    }

    if (currentPage === PAGES.ADMIN) {
        return (
            <div className={style.container}>
                <div className={style.wrap}>
                    <h1 className={style.pageTitle}>초기 설정</h1>
                    <h2>1. 관리자 계정 등록 (필수)</h2>
                    <div className={style.contentSection}>
                        <FormSection 
                            formControls={controls.adminForm} 
                            numOfCols={1} 
                        />
                        <div className={style.buttons}>
                            <Button
                                key="nextAdminBtn"
                                label={'다음으로'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('next')
                                }}
                                colorType={'main'}
                            />
                        </div>
                    </div>
                </div> 
            </div>
        );
    }

    if (currentPage === PAGES.CHAT) {
        return (
            <div className={style.container}>
                <div className={style.wrap}>
                    <h1 className={style.pageTitle}>초기 설정</h1>
                    <h2>2. LLM 등록[1/2]  - Chat 모델 (선택)</h2>
                    <div className={style.contentSection}>
                        <FormSection 
                            formControls={controls.modelForm} 
                            numOfCols={1} 
                        />
                        {dynamicFormControls?.formControls &&
                            Object.keys(dynamicFormControls.formControls).length > 0 && (
                                <div style={{ marginTop: '5px' }}>
                                    <FormSection
                                        key={Object.keys(dynamicFormControls.formControls).join('-')}
                                        {...dynamicFormControls}
                                    />
                                </div>
                            )}
                        <FormSection 
                            formControls={controls.switchForm} 
                            numOfCols={1} 
                        />
                        <div className={style.buttons}>
                            <Button
                                key="prevChatBtn"
                                label={'이전으로'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('prev')
                                }}
                                colorType={'sub2'}
                            />
                            <Button
                                key="nextChatBtn"
                                label={'다음으로'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('next')
                                }}
                                colorType={'main'}
                            />
                            <Button
                                key="skipChatBtn"
                                label={'건너뛰기'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('skip')
                                }}
                                colorType={'sub4'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (currentPage === PAGES.EMB) {
        return (
            <div className={style.container}>
                <div className={style.wrap}>
                    <h1 className={style.pageTitle}>초기 설정</h1>
                    <h2>3. LLM 등록[2/2] - Embedding 모델 (선택)</h2>
                    <div className={style.contentSection}>
                        <FormSection 
                            formControls={controls.modelForm} 
                            numOfCols={1} 
                        />
                        {dynamicFormControls?.formControls &&
                            Object.keys(dynamicFormControls.formControls).length > 0 && (
                                <div style={{ marginTop: '5px' }}>
                                    <FormSection
                                        key={Object.keys(dynamicFormControls.formControls).join('-')}
                                        {...dynamicFormControls}
                                    />
                                </div>
                            )}
                        <FormSection 
                            formControls={controls.switchForm} 
                            numOfCols={1} 
                        />
                        <div className={style.buttons}>
                            <Button
                                key="prevEmbBtn"
                                label={'이전으로'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('prev')
                                }}
                                colorType={'sub2'}
                            />
                            <Button
                                key={"nextEmbBtn"}
                                label={'다음으로'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('next')
                                }}
                                colorType={'main'}
                            />
                            <Button
                                key="skipEmbBtn"
                                label={'건너뛰기'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('skip')
                                }}
                                colorType={'sub4'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (currentPage === PAGES.CONFIRM) {
        return (
            <div className={style.container}>
                <div className={style.wrap}>
                    <h1 className={style.pageTitle}>초기 설정</h1>
                    <h2>4. 설정 확인</h2>
                    <div className={style.contentSection}>
                        <div className={style.sectionMargin}>
                            <h3>관리자 계정 정보</h3>
                            <div className={style.sectionBox}>
                                <FormSection 
                                    formControls={{
                                        txtUserId: {
                                            ...controls.adminForm.txtUserId,
                                            validation: { required: false },
                                            controlOption: {
                                                ...controls.adminForm.txtUserId.controlOption,
                                                defaultValue: formData.admin.USER_ID,
                                                readOnly: true,
                                            },
                                        },
                                        txtUserNm: {
                                            ...controls.adminForm.txtUserNm,
                                            validation: { required: false },
                                            controlOption: {
                                                ...controls.adminForm.txtUserNm.controlOption,
                                                defaultValue: formData.admin.USER_NM,
                                                readOnly: true,
                                            },
                                        },
                                        txtUserEmail: {
                                            ...controls.adminForm.txtUserEmail,
                                            validation: { required: false },
                                            controlOption: {
                                                ...controls.adminForm.txtUserEmail.controlOption,
                                                defaultValue: formData.admin.USER_EMAIL,
                                                readOnly: true,
                                            },
                                        },
                                    }} 
                                    numOfCols={1} 
                                />
                            </div>
                        </div>
                        
                        <div style={{ marginBottom: '30px' }}>
                            <h3>Chat 모델 정보</h3>
                            
                            {formData.chat ? (
                                <div className={style.sectionBox}>
                                    <FormSection 
                                        formControls={{
                                            txtCustomLlmProvider: {
                                                ...controls.modelForm.selCustomLlmProvider,
                                                validation: { required: false },
                                                controlOption: {
                                                    ...controls.modelForm.selCustomLlmProvider.controlOption,
                                                    defaultValue: formData.chat?.CUSTOM_LLM_PROVIDER,
                                                    readOnly: true,
                                                },
                                            },
                                            txtCustomModelName: {
                                                ...controls.modelForm.txtCustomModelName,
                                                validation: { required: false },
                                                controlOption: {
                                                    ...controls.modelForm.txtCustomModelName.controlOption,
                                                    defaultValue: formData.chat?.CUSTOM_MODEL_NAME,
                                                    readOnly: true,
                                                },
                                            },
                                            txtModelName: {
                                                ...controls.modelForm.txtModelName,
                                                validation: { required: false },
                                                controlOption: {
                                                    ...controls.modelForm.txtModelName.controlOption,
                                                    defaultValue: formData.chat?.MODEL_NAME,
                                                    readOnly: true,
                                                },
                                            },
                                        }} 
                                        numOfCols={1} 
                                    />
                                </div>
                            ) : (
                                <div className={style.sectionBox}>
                                    <p style={{ margin: 0, color: '#76787C' }}>LLM 관리에서 모델을 등록하세요</p>
                                </div>
                            )}
                        </div>
                        
                        <div style={{ marginBottom: '30px' }}>
                            <h3>Embedding 모델 정보</h3>
                            {formData.emb ? (
                                <div className={style.sectionBox}>
                                    <FormSection 
                                        formControls={{
                                            txtCustomLlmProvider: {
                                                ...controls.modelForm.selCustomLlmProvider,
                                                validation: { required: false },
                                                controlOption: {
                                                    ...controls.modelForm.selCustomLlmProvider.controlOption,
                                                    defaultValue: formData.emb?.CUSTOM_LLM_PROVIDER,
                                                    readOnly: true,
                                                },
                                            },
                                            txtCustomModelName: {
                                                ...controls.modelForm.txtCustomModelName,
                                                validation: { required: false },
                                                controlOption: {
                                                    ...controls.modelForm.txtCustomModelName.controlOption,
                                                    defaultValue: formData.emb?.CUSTOM_MODEL_NAME,
                                                    readOnly: true,
                                                },
                                            },
                                            txtModelName: {
                                                ...controls.modelForm.txtModelName,
                                                validation: { required: false },
                                                controlOption: {
                                                    ...controls.modelForm.txtModelName.controlOption,
                                                    defaultValue: formData.emb?.MODEL_NAME,
                                                    readOnly: true,
                                                },
                                            },
                                        }} 
                                        numOfCols={1} 
                                    />
                                </div>
                            ) : (
                                <div className={style.sectionBox}>
                                    <p style={{ margin: 0,  color: '#76787C' }}>LLM 관리에서 모델을 등록하세요</p>
                                </div>
                            )}
                        </div>
                        
                        <div className={style.buttons}>
                            <Button
                                key="prevConfirmBtn"
                                label={'이전으로'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.pageSetting('prev')
                                }}
                                colorType={'sub2'}
                            />
                            <Button
                                key="saveBtn"
                                label={'저장'}
                                checkAuth={false}
                                onClick={() => {
                                    pageFunctions.saveInitData()
                                }}
                                colorType={'main'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={style.container}>
            <h2>초기 설정화면</h2>
            <span>서비스 이용을 위해 플랫폼 초기 설정을 완료해 주세요.</span>
            <div>
                <Button
                    label={'초기 설정 시작'}
                    checkAuth={false}
                    onClick={() => {
                        setCurrentPage(PAGES.ADMIN)
                    }}
                    colorType={'main'}
                />
            </div>
        </div>
    );
}
export default InitSetting;
